import { Link } from "react-router";
import { HoverCard } from "@omnabase/ui";
import type { Project } from "@omnabase/shared";
import { canProject, useCan } from "../../lib/access";
import { useWs } from "../../lib/nav";
import { appliedSignature, signatureHtml, signatureVarsFor } from "../../lib/signature";
import { useSession } from "../../shell/store";
import { SignaturePreview } from "../settings/SignatureBlock";

/** The signature switch round (2026-10-28): the composer's signature choice, on for every opened composer and never stored; its owner (the
 *  ticket page, the new ticket) resets it and sends `signature: false` while it is off. */
export type SignatureSwitch = { on: boolean; onChange: (on: boolean) => void };

/** The signature as one part of the send line (the attachments round, 2026-10-28, rule 5; it replaced the "Added when sent" block): the
 *  words say whether one goes ("with signature", "without signature" while off; the send-line patch of 2026-10-29 shortened them) and are a
 *  switch (`role="switch"`, named "Signature: …") that looks like the line's other buttons; a workspace and project without any signature
 *  read "no signature" with an "Edit the workspace signature" link for someone who may set one. The small "Show the signature" button
 *  (not the words: no hover card) shows the filled signature as the mail will carry it and, for someone who may edit it, where it is
 *  edited: the project's page for its own signature, Settings › General for the workspace's. */
export function SignaturePart({ project, signature }: { project: Project | null | undefined; signature: SignatureSwitch }) {
  const { workspace, me } = useSession(); const { ws } = useWs(); const can = useCan();
  const template = appliedSignature(workspace, project);
  const own = !!project?.ownSignature;
  const editHref = own ? (project && canProject(workspace, project) ? ws(`/settings/projects/${project.id}`) : null) : can("aiSettings") ? ws("/settings/general") : null;
  // the send-line patch (the founder, 2026-10-29): short words, a plain button like the line's others, the card only behind the small show
  // button; with no signature anywhere the line says so and, for someone who may set one, links to the workspace signature
  if (!template.trim()) return <><span className="ob-sendline__word">no signature</span>{!own && editHref && <><span className="ob-sendline__sep" aria-hidden="true">{" · "}</span><Link className="ob-sendline__link" to={editHref}>Edit the workspace signature</Link></>}</>;
  const words = signature.on ? "with signature" : "without signature";
  const card = (
    <div className="ob-sigcard">
      {!signature.on && <div className="ob-sigcard__off">Left off this mail</div>}
      <div className="ob-sigcard__preview"><SignaturePreview html={signatureHtml(template, signatureVarsFor(workspace, me, project))} /></div>
      {editHref && <Link className="ob-sigcard__edit" to={editHref}>{own ? "Edit the project signature" : "Edit the workspace signature"}</Link>}
    </div>
  );
  return (
    <HoverCard label="The signature on this mail" showLabel="Show the signature" card={card} hoverOpens={false}>
      <button type="button" role="switch" aria-checked={signature.on} aria-label={`Signature: ${words}`} className="ob-sendline__btn ob-sendline__sig" onClick={() => signature.onChange(!signature.on)}>{words}</button>
    </HoverCard>
  );
}
